import React, { useEffect, useState } from "react";
import {
  Space,
  Row,
  Col,
  notification,
  Table,
  TableColumnsType,
  Typography,
  Button,
  message,
  Spin,
  Tooltip,
  Select,
  Tag,
  Progress,
  Modal,
  Dropdown,
  Statistic,
  Empty,
  Tabs,
} from "antd";
import { useParams, Link, useNavigate } from "react-router-dom";
import { TestSetService } from "../../services/TestSetService";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import { ChatCompletionRequest } from "../../model_gen/chat";
import {
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  SwapOutlined,
  FileTextOutlined,
  CalendarOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  FileSearchOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { TestSet, TestRun, IntentGroup } from "../../model_gen/evaluation";
import { NEW_TEST_RUN_STORAGE_KEY } from "../TestRun/TestRunNew";
import { TestRunService } from "../../services/TestRunService";
import { IntentService } from "../../services/IntentService";
import { MultiLayerSemanticChart } from "../shared/MultiLayerSemanticChart";
import TestRunComparison from "../TestRun/TestRunComparison";
import { RequestViewButton } from "../shared/button/Buttons";
const { Text } = Typography;
const { TabPane } = Tabs;

const TestSetDetail: React.FC = () => {
  const { intentGroupId, testSetId } = useParams();
  const { apiKey } = usePermissions();
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [requests, setRequests] = useState<ChatCompletionRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(5);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, ChatCompletionRequest[]>
  >({});
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [isTestRunsLoading, setIsTestRunsLoading] = useState<boolean>(false);
  const [currentTestRunPage, setCurrentTestRunPage] = useState<number>(1);
  const [testRunsByPage, setTestRunsByPage] = useState<
    Record<number, TestRun[]>
  >({});
  const [totalTestRuns, setTotalTestRuns] = useState<number>(0);
  const [tagDistribution, setTagDistribution] = useState<any[]>([]);
  const [testRunProgress, setTestRunProgress] = useState<Record<string, any>>(
    {}
  );
  const navigate = useNavigate();
  const [isCompareMode, setIsCompareMode] = useState<boolean>(false);
  const [selectedTestRunIds, setSelectedTestRunIds] = useState<number[]>([]);
  const [selectedTestRunsMap, setSelectedTestRunsMap] = useState<
    Record<number, boolean>
  >({});
  const [isComparisonModalVisible, setIsComparisonModalVisible] =
    useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("overview");

  const styles = {
    hoverRow: {
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
    statsCard: (color: string) => ({
      background: `linear-gradient(135deg, ${color}10 0%, #f8f8f8 100%)`,
      padding: "24px",
      borderRadius: "12px",
      height: "100%",
      border: `1px solid ${color}30`,
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
      transition: "all 0.3s ease",
    }),
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    detailsContainer: {
      borderRadius: "12px",
      border: "1px solid #f0f0f0",
      overflow: "hidden",
    },
    detailsHeader: {
      borderBottom: "1px solid #f0f0f0",
      padding: "16px 24px",
      background: "#fafafa",
    },
    detailsContent: {
      padding: "24px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    tagStyle: {
      padding: "4px 10px",
      borderRadius: "4px",
      fontSize: "12px",
    },
  };

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        false,
        setIsLoading,
        apiKey
      ).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (testSetId) {
      setIsLoading(true);
      // Fetch the TestSet
      TestSetService.getTestSet(testSetId, setIsLoading, apiKey).subscribe(
        (data: TestSet) => {
          setTestSet(data);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test set: ${error}`,
          });
          setIsLoading(false);
        }
      );
      if (!requestsByPage[currentPage]) {
        fetchRequests(currentPage);
      } else {
        setRequests(requestsByPage[currentPage]);
      }
    }
  }, [testSetId, currentPage, requestPageSize]);

  useEffect(() => {
    if (testSetId && !testRunsByPage[currentTestRunPage]) {
      fetchTestRuns(currentTestRunPage);
    } else if (testRunsByPage[currentTestRunPage]) {
      setTestRuns(testRunsByPage[currentTestRunPage]);
    }
  }, [testSetId, currentTestRunPage]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSetRequestDistribution(
        testSetId,
        null,
        apiKey
      ).subscribe(
        (response: any[]) => {
          setTagDistribution(response);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch tag distribution: ${error}`,
          });
        }
      );
    }
  }, [testSetId]);

  useEffect(() => {
    const fetchProgress = () => {
      if (!testSetId) return;

      TestRunService.getTestRunProgressByTestSet(
        Number(testSetId),
        null,
        apiKey
      ).subscribe(
        (progressData: Record<string, any>) => {
          setTestRunProgress(progressData);
        },
        (error: any) => {
          console.error(error);
        }
      );
    };

    // Check if any test runs need monitoring
    const hasIncompleteRuns = testRuns.some(
      (run) => run.status !== "COMPLETED" && run.status !== "ERROR"
    );

    // Only set up interval if there are incomplete runs
    if (hasIncompleteRuns) {
      // Run immediately
      fetchProgress();

      // Then set up interval
      const intervalId = setInterval(fetchProgress, 5000);

      // Cleanup on unmount
      return () => clearInterval(intervalId);
    }
  }, [testSetId, apiKey, testRuns]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * requestPageSize;
    setIsRequestsLoading(true);
    TestSetService.getTestSetRequests(
      testSetId,
      -1,
      -1,
      offset,
      requestPageSize,
      null,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setRequests(response.items);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsRequestsLoading(false);
      }
    );
  };

  const fetchTestRuns = (page: number) => {
    const offset = (page - 1) * 5;
    setIsTestRunsLoading(true);
    TestRunService.getTestRunsByTestSet(
      Number(testSetId),
      offset,
      5,
      null,
      apiKey
    ).subscribe(
      (response: { items: TestRun[]; total: number }) => {
        setTestRuns(response.items);
        setTotalTestRuns(response.total);
        setTestRunsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsTestRunsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch test runs: ${error}`,
        });
        setIsTestRunsLoading(false);
      }
    );
  };

  const handleTagsChange = (requestId: string, newTags: string[]) => {
    setIsRequestsLoading(true);
    const request = requests.find((r) => r.request_id === requestId);
    if (!request) return;

    try {
      const metadata = JSON.parse(
        (request.metadata?._maitai_test_request_meta as string) || "{}"
      );
      metadata.tags = newTags;

      TestSetService.updateTestSetRequestTags(
        testSetId,
        requestId,
        newTags,
        null,
        apiKey
      ).subscribe(
        () => {
          // Update the local state
          setRequests((prev) =>
            prev.map((r) => {
              if (r.request_id === requestId) {
                return {
                  ...r,
                  metadata: {
                    ...r.metadata,
                    _maitai_test_request_meta: JSON.stringify(metadata),
                  },
                };
              }
              return r;
            })
          );
          setIsRequestsLoading(false);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to update tags",
          });
          setIsRequestsLoading(false);
        }
      );
    } catch (e) {
      console.error(e);
      setIsRequestsLoading(false);
    }
  };

  const updateTestSetRequest = (request: ChatCompletionRequest) => {
    setRequests((prev) =>
      prev.map((r) => (r.request_id === request.request_id ? request : r))
    );
  };

  const handleNewTestRun = () => {
    // Clear any existing test run data
    localStorage.removeItem(NEW_TEST_RUN_STORAGE_KEY);
    // Navigate to new test run page
    navigate(
      `/intent-group/${testSet.intent_group_id}/testsets/${testSetId}/runs/new`
    );
  };

  const handleDeleteTestRun = (testRun: TestRun) => {
    Modal.confirm({
      title: "Delete Test Run",
      content: "Are you sure you want to delete this test run?",
      okText: "Yes",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: () => {
        TestRunService.deleteTestRun(testRun.id, null, apiKey).subscribe(
          () => {
            message.success("Test run deleted successfully");
            // Clear the test runs page cache
            setTestRunsByPage({});
            // Reset to first page
            setCurrentTestRunPage(1);
            // Fetch fresh data
            fetchTestRuns(1);
          },
          (error: any) => {
            console.error(error);
            notification.error({
              message: "Error",
              description: `Failed to delete test run: ${error}`,
            });
          }
        );
      },
    });
  };

  const handleCompareClick = () => {
    setIsCompareMode(true);
    setSelectedTestRunIds([]);
    setSelectedTestRunsMap({});
  };

  const handleCancelCompare = () => {
    setIsCompareMode(false);
    setSelectedTestRunIds([]);
    setSelectedTestRunsMap({});
  };

  const handleCompareRuns = () => {
    setIsComparisonModalVisible(true);
  };

  const handleCloseComparisonModal = () => {
    setIsComparisonModalVisible(false);
    // Reset comparison mode and selected test runs when modal closes
    setIsCompareMode(false);
    setSelectedTestRunIds([]);
    setSelectedTestRunsMap({});
  };

  // Add global CSS for interactive rows
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .interactive-row {
        cursor: pointer;
        transition: all 0.2s ease;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        background-color: #f5f8ff !important;
      }
      .ant-statistic-content-value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .ant-modal-content {
        border-radius: 12px;
        overflow: hidden;
      }
      .ant-table {
        border-radius: 8px;
        overflow: hidden;
      }
      .ant-statistic-title {
        margin-bottom: 8px !important;
      }
      .ant-tag {
        border-radius: 4px;
        padding: 3px 8px;
      }
      .test-set-container .ant-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
      }
      .test-set-container .ant-btn {
        transition: all 0.2s ease;
      }
      .custom-tabs .ant-tabs-tab {
        transition: all 0.3s ease;
        border-radius: 6px 6px 0 0 !important;
        padding: 8px 16px !important;
      }
      .custom-tabs .ant-tabs-tab:hover {
        color: #1890ff;
      }
      .custom-tabs .ant-tabs-tab-active {
        background-color: #f0f7ff !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const requestColumns: TableColumnsType<ChatCompletionRequest> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Tags",
      key: "tags",
      width: "50%",
      render: (_, request: ChatCompletionRequest) => {
        try {
          const metaData = JSON.parse(
            (request.metadata?._maitai_test_request_meta as string) || "{}"
          );
          const tags = metaData.tags || [];
          return (
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Add tags"
              defaultValue={tags}
              tokenSeparators={[","]}
              options={tagDistribution.map(({ tag }) => ({
                label: tag,
                value: tag,
              }))}
              onClick={(e) => e.stopPropagation()}
              maxTagTextLength={10}
              maxTagCount={5}
              onBlur={(e) => {
                const selectContainer = e.target.closest(".ant-select");
                if (!selectContainer) return;

                const selectedItems = selectContainer.querySelectorAll(
                  ".ant-select-selection-item"
                );
                const newTags = Array.from(selectedItems).map(
                  (item: any) => item.textContent || ""
                );

                if (JSON.stringify(newTags) !== JSON.stringify(tags)) {
                  handleTagsChange(request.request_id, newTags);
                }
              }}
            />
          );
        } catch (e) {
          return null;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "20%",
      render: (_, request: ChatCompletionRequest) => {
        const requestId = request.request_id || "";
        const shortId =
          requestId.length > 12
            ? `${requestId.substring(0, 6)}...${requestId.substring(
                requestId.length - 6
              )}`
            : requestId;

        return (
          <Space>
            <Typography.Text style={styles.monospaceFont} copyable={false}>
              {shortId}
            </Typography.Text>
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success({
                  content: "Request ID copied to clipboard",
                  icon: <CopyOutlined style={{ color: "#52c41a" }} />,
                });
              }}
            />
          </Space>
        );
      },
    },

    {
      title: "Actions",
      key: "actions",
      width: "15%",
      align: "center",
      render: (_, record: ChatCompletionRequest) => (
        <RequestViewButton
          request={record}
          apiKey={apiKey}
          testSetId={testSetId}
          testSetRequestId={record.metadata?._maitai_test_request_id as string}
          isEditable={true}
          onUpdated={(request: ChatCompletionRequest) => {
            updateTestSetRequest(request);
          }}
          to={`/application/${
            record.application_id
          }/session/${encodeURIComponent(record.session_id)}/request/${
            record.request_id
          }`}>
          Details
        </RequestViewButton>
      ),
    },
  ];

  const testRunColumns: TableColumnsType<TestRun> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text: string) => text || "-",
    },
    {
      title: "Pass Rate",
      key: "pass_rate",
      width: "20%",
      render: (_, record: TestRun) => {
        const summary = record.qualitative_evaluation_summary;
        if (!summary?.request_scores?.length || !summary.completed_requests) {
          return "-";
        }

        const passingRequests = summary.request_scores
          .filter((score: any) => score.score >= 3)
          .reduce((acc: any, score: any) => acc + score.count, 0);

        const passRate = (passingRequests / summary.completed_requests) * 100;

        return (
          <Tooltip
            title={`${passingRequests} out of ${summary.completed_requests} completed requests scored 3 or higher`}>
            <Tag
              color={
                passRate >= 80
                  ? "success"
                  : passRate >= 60
                  ? "warning"
                  : "error"
              }
              style={styles.tagStyle}>
              {passRate.toFixed(1)}%
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Avg Time",
      key: "avg_time",
      width: "15%",
      render: (_, record: TestRun) => {
        const summary = record.qualitative_evaluation_summary;
        if (!summary?.response_time_percentiles?.p50) {
          return "-";
        }
        return (
          <Tooltip
            title={
              <div>
                <div>
                  P50: {Math.round(summary.response_time_percentiles.p50)}ms
                </div>
                <div>
                  P90: {Math.round(summary.response_time_percentiles.p90)}ms
                </div>
                <div>
                  P95: {Math.round(summary.response_time_percentiles.p95)}ms
                </div>
              </div>
            }>
            <Text>{Math.round(summary.response_time_percentiles.p50)}ms</Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: "15%",
      render: (_, record: TestRun) => {
        const progressData = testRunProgress[record.id.toString()];
        const status = progressData?.status || record.status;

        const statusConfig: Record<string, { color: string }> = {
          CREATED: { color: "default" },
          COMPLETED: { color: "success" },
          ERROR: { color: "error" },
          PENDING: { color: "processing" },
        };

        if (status !== "RUNNING") {
          return (
            <Tag
              color={statusConfig[status]?.color || "default"}
              style={styles.tagStyle}>
              {status}
            </Tag>
          );
        }

        if (!progressData) {
          return <Spin size="small" />;
        }

        const {
          completed_requests,
          error_requests,
          completed_evals,
          total_requests,
        } = progressData;

        let percent = 0;
        if (!total_requests) {
          percent = 0;
        } else if (completed_evals > 0) {
          percent =
            completed_requests > 0
              ? 50 + Math.min(50, (completed_evals / completed_requests) * 50)
              : 0;
        } else {
          percent = (completed_requests / total_requests) * 50;
        }
        percent = Math.min(percent, 99.9);

        const tooltipText =
          completed_evals > 0
            ? `${completed_evals}/${completed_requests} evaluations completed`
            : `${completed_requests}/${total_requests} requests completed with ${error_requests} errors`;

        return (
          <Tooltip title={tooltipText}>
            <Progress
              percent={Number(percent.toFixed(1))}
              size="small"
              status="active"
            />
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "5%",
      render: (_, record: TestRun) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleDeleteTestRun(record);
                },
              },
            ],
          }}
          trigger={["click"]}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderOverviewTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Stats Row */}
      <Row gutter={16}>
        <Col span={8}>
          <div style={styles.statsCard("#1890ff")}>
            <Statistic
              title={
                <Typography.Text style={{ fontSize: "14px", color: "#8c8c8c" }}>
                  Total Requests
                </Typography.Text>
              }
              value={(testSet?.meta?.request_count as number) || 0}
              prefix={
                <FileTextOutlined
                  style={{
                    color: "#1890ff",
                    marginRight: "8px",
                    fontSize: "20px",
                  }}
                />
              }
              valueStyle={{
                fontSize: "28px",
                fontWeight: 600,
                color: "#262626",
                display: "flex",
                alignItems: "center",
              }}
            />
          </div>
        </Col>
        <Col span={8}>
          <div style={styles.statsCard("#52c41a")}>
            <Statistic
              title={
                <Typography.Text style={{ fontSize: "14px", color: "#8c8c8c" }}>
                  Date Created
                </Typography.Text>
              }
              value={moment(testSet?.date_created).format("MMM D, YYYY")}
              prefix={
                <CalendarOutlined
                  style={{
                    color: "#52c41a",
                    marginRight: "8px",
                    fontSize: "20px",
                  }}
                />
              }
              valueStyle={{
                fontSize: "28px",
                fontWeight: 600,
                color: "#262626",
                display: "flex",
                alignItems: "center",
              }}
            />
          </div>
        </Col>
        <Col span={8}>
          <div style={styles.statsCard("#722ed1")}>
            <Tooltip title="Golden test sets are the default test sets used to test new models">
              <Statistic
                title={
                  <Typography.Text
                    style={{ fontSize: "14px", color: "#8c8c8c" }}>
                    Golden Status
                  </Typography.Text>
                }
                value={(testSet as any)?.is_golden ? "Golden" : "Not Golden"}
                prefix={
                  <StarOutlined
                    style={{
                      color: "#722ed1",
                      marginRight: "8px",
                      fontSize: "20px",
                    }}
                  />
                }
                valueStyle={{
                  fontSize: "28px",
                  fontWeight: 600,
                  color: "#262626",
                  display: "flex",
                  alignItems: "center",
                }}
              />
            </Tooltip>
          </div>
        </Col>
      </Row>

      {/* Details Section */}
      <div style={styles.detailsContainer}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Details
          </Typography.Title>
        </div>
        <div style={styles.detailsContent}>
          <Row gutter={[0, 24]}>
            <Col span={24}>
              <Typography.Text style={styles.subtitle}>
                Description
              </Typography.Text>
              <Typography.Paragraph
                style={{
                  whiteSpace: "pre-wrap",
                  margin: 0,
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}>
                {testSet?.description || "No description provided"}
              </Typography.Paragraph>
            </Col>
            {tagDistribution.length > 0 && (
              <Col span={24}>
                <Typography.Text style={styles.subtitle}>
                  Request Distribution
                </Typography.Text>
                <div>
                  <Space wrap size={8}>
                    {tagDistribution.map(({ tag, count }) => (
                      <Tag key={tag} color="blue" style={styles.tagStyle}>
                        {tag} ({count})
                      </Tag>
                    ))}
                  </Space>
                </div>
              </Col>
            )}
          </Row>
        </div>
      </div>

      {/* Semantic Distribution Chart */}
      {testSet && (
        <MultiLayerSemanticChart
          chartData={[
            {
              entityType: "test_set",
              entityTypeId: testSetId || "",
              name: testSet?.name,
            },
          ]}
          apiKey={apiKey}
          title="Semantic Distribution"
        />
      )}
    </div>
  );

  const renderTestRunsTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Test Runs
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            {!isCompareMode ? (
              <>
                <Button icon={<SwapOutlined />} onClick={handleCompareClick}>
                  Compare Runs
                </Button>
                <Button type="primary" onClick={handleNewTestRun}>
                  New Test Run
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleCancelCompare}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={handleCompareRuns}
                  disabled={selectedTestRunIds.length < 2}>
                  Compare{" "}
                  {selectedTestRunIds.length > 0
                    ? `(${selectedTestRunIds.length})`
                    : ""}
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>

      <div style={styles.tableContainer}>
        <Table
          rowKey="id"
          columns={testRunColumns}
          dataSource={testRuns}
          loading={isTestRunsLoading}
          rowSelection={
            isCompareMode
              ? {
                  type: "checkbox",
                  onChange: (selectedRowKeys, selectedRows) => {
                    // Create a new selectedTestRunsMap that combines existing selections with current page selections
                    const updatedMap = { ...selectedTestRunsMap };

                    // Mark all items on current page as unselected
                    testRuns.forEach((run) => {
                      updatedMap[run.id] = false;
                    });

                    // Then mark selected rows as selected
                    selectedRows.forEach((row) => {
                      updatedMap[row.id] = true;
                    });

                    setSelectedTestRunsMap(updatedMap);

                    // Update the array of selected IDs from the map for use in comparison
                    const allSelectedIds = Object.entries(updatedMap)
                      .filter(([_, isSelected]) => isSelected)
                      .map(([id, _]) => Number(id));

                    setSelectedTestRunIds(allSelectedIds);
                  },
                  selectedRowKeys: testRuns
                    .filter((run) => selectedTestRunsMap[run.id])
                    .map((run) => run.id),
                  getCheckboxProps: (record) => {
                    const recordStatus: string =
                      testRunProgress[record.id.toString()]?.status ||
                      record.status;
                    return {
                      disabled:
                        recordStatus === "CREATED" ||
                        recordStatus === "PENDING" ||
                        recordStatus === "RUNNING",
                    };
                  },
                }
              : undefined
          }
          pagination={{
            current: currentTestRunPage,
            pageSize: 5,
            total: totalTestRuns,
            onChange: (page) => setCurrentTestRunPage(page),
          }}
          onRow={(record) => {
            const recordStatus: string =
              testRunProgress[record.id.toString()]?.status || record.status;
            return {
              onClick: () => {
                if (
                  !isCompareMode &&
                  recordStatus !== "CREATED" &&
                  recordStatus !== "PENDING" &&
                  recordStatus !== "RUNNING"
                ) {
                  navigate(
                    `/intent-group/${testSet?.intent_group_id}/testsets/${testSetId}/runs/${record.id}`
                  );
                }
              },
              style: {
                cursor: isCompareMode
                  ? "default"
                  : recordStatus !== "CREATED" &&
                    recordStatus !== "PENDING" &&
                    recordStatus !== "RUNNING"
                  ? "pointer"
                  : "not-allowed",
                opacity:
                  recordStatus === "CREATED" ||
                  recordStatus === "PENDING" ||
                  recordStatus === "RUNNING"
                    ? 0.75
                    : 1,
                backgroundColor:
                  recordStatus === "CREATED" ||
                  recordStatus === "PENDING" ||
                  recordStatus === "RUNNING"
                    ? "#f5f5f5"
                    : "inherit",
              },
            };
          }}
          rowClassName={() => "interactive-row"}
          locale={{
            emptyText: (
              <Empty
                description={
                  <div>
                    <Typography.Text
                      strong
                      style={{ display: "block", marginBottom: "8px" }}>
                      No test runs found
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Create a new test run to get started.
                    </Typography.Text>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>
    </div>
  );

  const renderRequestsTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div style={styles.tableContainer}>
        <Table
          dataSource={requests}
          columns={requestColumns}
          rowKey="request_id"
          loading={isRequestsLoading}
          rowClassName={() => "interactive-row"}
          pagination={{
            current: currentPage,
            pageSize: requestPageSize,
            total: Number(testSet?.meta?.request_count) || 0,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              if (pageSize && pageSize !== requestPageSize) {
                setRequestPageSize(pageSize);
                setRequestsByPage({});
              }
            },
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onShowSizeChange: (current, size) => {
              setCurrentPage(1);
              setRequestPageSize(size);
              setRequestsByPage({});
            },
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <div>
                    <Typography.Text
                      strong
                      style={{ display: "block", marginBottom: "8px" }}>
                      No requests found
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Add requests to this test set to get started.
                    </Typography.Text>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>
    </div>
  );

  return (
    <div
      className="test-set-container"
      style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/intent-groups">Intent Groups</Link> /{" "}
                <Link to={`/intent-group/${testSet?.intent_group_id}`}>
                  {intentGroup?.intent_name}
                </Link>{" "}
                /{" "}
                <Link to={`/intent-group/${testSet?.intent_group_id}/testsets`}>
                  Test Sets
                </Link>{" "}
                / {testSet?.name}
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                {testSet?.name}
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Manage test requests, run evaluations, and analyze model
                performance
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        className="custom-tabs"
        tabBarStyle={{ marginBottom: "24px" }}
        tabBarGutter={12}
        animated={{ inkBar: true, tabPane: true }}
        tabBarExtraContent={
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() =>
              navigate(
                `/intent-group/${testSet?.intent_group_id}/testsets/${testSetId}/edit`
              )
            }>
            Edit
          </Button>
        }>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <AppstoreOutlined style={{ marginRight: "8px" }} />
              Overview
            </span>
          }
          key="overview">
          <div style={styles.contentCard}>{renderOverviewTab()}</div>
        </TabPane>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <HistoryOutlined style={{ marginRight: "8px" }} />
              Test Runs
            </span>
          }
          key="test_runs">
          <div style={styles.contentCard}>{renderTestRunsTab()}</div>
        </TabPane>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <FileSearchOutlined style={{ marginRight: "8px" }} />
              Requests
            </span>
          }
          key="requests">
          <div style={styles.contentCard}>{renderRequestsTab()}</div>
        </TabPane>
      </Tabs>

      {/* Comparison Modal */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontWeight: 600,
              borderBottom: "1px solid #f0f0f0",
              padding: "16px 24px",
              margin: "-20px -24px 20px",
            }}>
            <SwapOutlined />
            <span>Test Run Comparison</span>
          </div>
        }
        open={isComparisonModalVisible}
        onCancel={handleCloseComparisonModal}
        width={1000}
        footer={[
          <Button key="close" onClick={handleCloseComparisonModal}>
            Close
          </Button>,
        ]}
        destroyOnClose={true}
        style={{ top: 20 }}>
        <TestRunComparison
          testRunIds={selectedTestRunIds}
          apiKey={apiKey}
          key={selectedTestRunIds.join("-")}
        />
      </Modal>
    </div>
  );
};

export default TestSetDetail;
